import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Terminal, Zap, AlertTriangle, Activity } from "lucide-react";
import { SCENARIOS } from "@/lib/scenarioLibrary";
import {
  generateSimulationContent,
  type ContentFormat,
  type SimulationContent,
} from "@/lib/contentGenerator";
import {
  registerGeneratedBundle,
  logBundleGenerated,
  logBundleGenerationFailed,
  emitContentAssetsChanged,
} from "@/lib/contentTelemetry";
import { scoreAndPersist } from "@/lib/predictionEngine";

const FORMATS: { key: ContentFormat; label: string }[] = [
  { key: "replay", label: "Replay" },
  { key: "spiral", label: "Spiral" },
  { key: "pov", label: "POV" },
];

interface GeneratedAsset {
  assetId: string;
  content: SimulationContent;
}

interface GenerationSession {
  id: string;
  ts: number;
  scenarioTitle: string;
  formats: ContentFormat[];
  assetCount: number;
  durationMs: number;
  status: "ok" | "failed";
  error?: string;
}

const MAX_SESSIONS = 10;

export default function GenerateContentBundlePanel() {
  const [scenarioId, setScenarioId] = useState<string>(SCENARIOS[0]?.id ?? "");
  const [selectedFormats, setSelectedFormats] = useState<Set<ContentFormat>>(
    new Set(["replay", "spiral", "pov"])
  );
  const [isGenerating, setIsGenerating] = useState(false);
  const [results, setResults] = useState<GeneratedAsset[]>([]);
  const [sessions, setSessions] = useState<GenerationSession[]>([]);
  const [lastError, setLastError] = useState<string | null>(null);

  const scenario = useMemo(
    () => SCENARIOS.find((s) => s.id === scenarioId),
    [scenarioId]
  );

  const toggleFormat = (f: ContentFormat) => {
    setSelectedFormats((prev) => {
      const next = new Set(prev);
      next.has(f) ? next.delete(f) : next.add(f);
      return next;
    });
  };

  const pushSession = (s: GenerationSession) => {
    setSessions((prev) => [s, ...prev].slice(0, MAX_SESSIONS));
  };

  const handleGenerate = async () => {
    if (!scenario) {
      setLastError("Select a scenario before generating.");
      return;
    }
    if (selectedFormats.size === 0) {
      setLastError("Select at least one format.");
      return;
    }

    setLastError(null);
    setIsGenerating(true);
    setResults([]);
    const t0 = performance.now();
    const formats = FORMATS.map((f) => f.key).filter((f) =>
      selectedFormats.has(f)
    );

    try {
      const generated = formats.map((format) =>
        generateSimulationContent({ format, scenario })
      );
      const registered = await registerGeneratedBundle(generated, {
        platform: "internal",
      });

      const durationMs = Math.round(performance.now() - t0);
      const assetIds = registered.map((r) => r.assetId);

      // Predictive Viral Scoring — forecast performance pre-render
      void scoreAndPersist(
        registered.map((r) => ({
          assetId: r.assetId,
          archetype: r.content.archetype,
          format: r.content.format,
          platform: "internal",
          hook: r.content.hook,
        }))
      );

      // Telemetry — bundle-level event
      void logBundleGenerated({
        scenarioId: scenario.id,
        scenarioTitle: scenario.title,
        formats,
        assetIds,
        generationTimeMs: durationMs,
        platform: "internal",
      });

      setResults(
        registered.map((r) => ({ assetId: r.assetId, content: r.content }))
      );

      pushSession({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        ts: Date.now(),
        scenarioTitle: scenario.title,
        formats,
        assetCount: registered.length,
        durationMs,
        status: "ok",
      });

      // Auto-populate the queue (and any other listening surface).
      emitContentAssetsChanged({ assetIds });
    } catch (err: any) {
      const durationMs = Math.round(performance.now() - t0);
      const message = err?.message ?? "Unknown pipeline error";
      setLastError(message);

      void logBundleGenerationFailed({
        scenarioId: scenario.id,
        scenarioTitle: scenario.title,
        formats,
        error: message,
        generationTimeMs: durationMs,
      });

      pushSession({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        ts: Date.now(),
        scenarioTitle: scenario.title,
        formats,
        assetCount: 0,
        durationMs,
        status: "failed",
        error: message,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Terminal className="w-5 h-5 text-cyan" />
          Generate Content Bundle
          <Badge
            variant="outline"
            className="ml-2 text-[10px] uppercase tracking-wider border-cyan/40 text-cyan"
          >
            Ops Console
          </Badge>
          {isGenerating && (
            <span className="ml-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-mono text-cyan/80">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan animate-pulse" />
              Pipeline active
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Scenario selector */}
        <div className="space-y-2">
          <label className="text-xs uppercase tracking-wider text-muted-foreground">
            Scenario
          </label>
          <Select value={scenarioId} onValueChange={setScenarioId} disabled={isGenerating}>
            <SelectTrigger className="bg-charcoal-deep border-glass-border text-sm">
              <SelectValue placeholder="Select scenario" />
            </SelectTrigger>
            <SelectContent className="max-h-[320px]">
              {SCENARIOS.map((s) => (
                <SelectItem key={s.id} value={s.id} className="text-xs">
                  {s.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Format selection */}
        <div className="space-y-2">
          <label className="text-xs uppercase tracking-wider text-muted-foreground">
            Formats
          </label>
          <div className="flex flex-wrap gap-3">
            {FORMATS.map((f) => {
              const checked = selectedFormats.has(f.key);
              return (
                <label
                  key={f.key}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md border transition-colors ${
                    isGenerating ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
                  } ${
                    checked
                      ? "border-cyan/60 bg-cyan/10"
                      : "border-glass-border bg-charcoal-deep hover:border-cyan/30"
                  }`}
                >
                  <Checkbox
                    checked={checked}
                    disabled={isGenerating}
                    onCheckedChange={() => toggleFormat(f.key)}
                    className="data-[state=checked]:bg-cyan data-[state=checked]:border-cyan"
                  />
                  <span className="text-xs font-mono uppercase tracking-wider text-foreground">
                    {f.label}
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        {/* Generate */}
        <div className="flex items-center gap-3 flex-wrap">
          <Button
            onClick={handleGenerate}
            disabled={isGenerating}
            className={`bg-cyan/20 hover:bg-cyan/30 text-cyan border border-cyan/40 ${
              isGenerating ? "animate-pulse" : ""
            }`}
          >
            {isGenerating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Zap className="w-4 h-4 mr-2" />
            )}
            {isGenerating ? "Generating…" : "Generate Bundle"}
          </Button>
          {results.length > 0 && !isGenerating && (
            <span className="text-xs font-mono text-cyan/80 uppercase tracking-wider">
              {results.length} asset(s) committed · queue updated
            </span>
          )}
          {lastError && !isGenerating && (
            <span className="text-xs font-mono text-red-400 inline-flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" />
              Generation pipeline interrupted — {lastError}
            </span>
          )}
        </div>

        {/* Output preview */}
        {results.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
            {results.map(({ assetId, content }) => {
              const preview = content.dialogue
                .slice(0, 3)
                .map(
                  (t) =>
                    `${t.role === "you" ? "YOU" : t.role === "you_alt" ? "ALT" : "THEM"}: ${t.text}`
                );
              return (
                <Card
                  key={assetId}
                  className="bg-charcoal-deep/80 border-glass-border"
                >
                  <CardContent className="p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <Badge
                        variant="outline"
                        className="text-[10px] uppercase tracking-wider border-cyan/40 text-cyan font-mono"
                      >
                        {content.format}
                      </Badge>
                      <span className="text-[10px] font-mono text-muted-foreground truncate max-w-[120px]">
                        {assetId.slice(0, 8)}
                      </span>
                    </div>
                    <div className="text-sm font-medium text-foreground leading-snug">
                      {content.hook}
                    </div>
                    <div className="space-y-1 pt-1 border-t border-glass-border">
                      {preview.map((line, i) => (
                        <div
                          key={i}
                          className="text-[11px] font-mono text-muted-foreground leading-relaxed"
                        >
                          {line}
                        </div>
                      ))}
                    </div>
                    <div className="pt-2 border-t border-glass-border">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        CTA
                      </div>
                      <div className="text-xs text-cyan/90">{content.cta}</div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Generation Activity log */}
        <div className="pt-2 border-t border-glass-border">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-3.5 h-3.5 text-cyan" />
            <span className="text-[10px] uppercase tracking-wider font-mono text-cyan">
              Generation Activity
            </span>
            <Badge
              variant="outline"
              className="text-[10px] uppercase tracking-wider border-glass-border text-muted-foreground font-mono"
            >
              latest {Math.min(sessions.length, MAX_SESSIONS)}
            </Badge>
          </div>
          {sessions.length === 0 ? (
            <div className="text-[11px] font-mono text-muted-foreground py-2">
              No sessions yet. Pipeline idle.
            </div>
          ) : (
            <div className="divide-y divide-glass-border border border-glass-border rounded-md bg-charcoal-deep/50">
              {sessions.map((s) => (
                <div
                  key={s.id}
                  className="px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-mono"
                >
                  <span className="text-muted-foreground tabular-nums">
                    {new Date(s.ts).toLocaleTimeString()}
                  </span>
                  <span
                    className={`uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                      s.status === "ok"
                        ? "border-cyan/40 text-cyan bg-cyan/10"
                        : "border-red-500/50 text-red-400 bg-red-500/10"
                    }`}
                  >
                    {s.status === "ok" ? "ok" : "failed"}
                  </span>
                  <span className="text-foreground/90 truncate max-w-[260px]">
                    {s.scenarioTitle}
                  </span>
                  <span className="text-muted-foreground uppercase">
                    {s.formats.join(" · ")}
                  </span>
                  <span className="text-muted-foreground ml-auto tabular-nums">
                    {s.assetCount} asset{s.assetCount === 1 ? "" : "s"} · {s.durationMs}ms
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
